import React, { createContext, useContext, useRef, useCallback } from 'react';

/**
 * Global Cache Manager for TioAssist Chat
 * Prevents duplicate Firestore reads by caching frequently accessed data
 */

const CacheContext = createContext();

export const useCache = () => {
  const context = useContext(CacheContext);
  if (!context) {
    throw new Error('useCache must be used within CacheProvider');
  }
  return context;
};

export const CacheProvider = ({ children }) => {
  // Use refs to persist cache across re-renders
  const cacheRef = useRef({
    userProfiles: new Map(),    // userId -> {data, timestamp}
    chatMetadata: new Map(),    // chatId -> {data, timestamp}
    presence: new Map(),        // userId -> {data, timestamp}
    messages: new Map(),        // chatId -> {messages, timestamp}
    unreadCounts: new Map()     // userId -> {counts, timestamp}
  });

  // Cache duration configuration
  const CACHE_DURATION = {
    userProfiles: 10 * 60 * 1000,  // 10 minutes - profiles rarely change
    chatMetadata: 5 * 60 * 1000,   // 5 minutes - chat metadata
    presence: 2 * 60 * 1000,       // 2 minutes - presence updates
    messages: 30 * 60 * 1000,      // 30 minutes - old messages don't change
    unreadCounts: 1 * 60 * 1000    // 1 minute - unread counts
  };

  // Statistics tracking
  const statsRef = useRef({
    hits: 0,
    misses: 0,
    sets: 0
  });

  /**
   * Get cached data if still valid
   */
  const getCachedData = useCallback((cacheType, key) => {
    const cache = cacheRef.current[cacheType];
    if (!cache) {
      console.warn(`Invalid cache type: ${cacheType}`);
      return null;
    }

    const cached = cache.get(key);

    if (cached && Date.now() - cached.timestamp < CACHE_DURATION[cacheType]) {
      statsRef.current.hits++;
      return cached.data;
    }

    statsRef.current.misses++;
    return null;
  }, []);

  /**
   * Set data in cache with timestamp
   */
  const setCachedData = useCallback((cacheType, key, data) => {
    const cache = cacheRef.current[cacheType];
    if (!cache) {
      console.warn(`Invalid cache type: ${cacheType}`);
      return;
    }

    cache.set(key, {
      data,
      timestamp: Date.now()
    });

    statsRef.current.sets++;
  }, []);

  /**
   * Check if cache has valid data without returning it
   */
  const hasCachedData = useCallback((cacheType, key) => {
    const cached = getCachedData(cacheType, key);
    return cached !== null;
  }, [getCachedData]);

  /**
   * Clear specific cache or all caches
   */
  const clearCache = useCallback((cacheType = null) => {
    if (cacheType) {
      const cache = cacheRef.current[cacheType];
      if (cache) {
        cache.clear();
      }
    } else {
      Object.entries(cacheRef.current).forEach(([ cache]) => {
        cache.clear();
      });
    }
  }, []);

  /**
   * Get cache statistics
   */
  const getCacheStats = useCallback(() => {
    const stats = {
      ...statsRef.current,
      hitRate: statsRef.current.hits / (statsRef.current.hits + statsRef.current.misses) || 0,
      sizes: {}
    };

    Object.entries(cacheRef.current).forEach(([type, cache]) => {
      stats.sizes[type] = cache.size;
    });

    return stats;
  }, []);

  /**
   * Batch get multiple items from cache
   */
  const getBatchCachedData = useCallback((cacheType, keys) => {
    const results = {};
    const missingKeys = [];

    keys.forEach(key => {
      const cached = getCachedData(cacheType, key);
      if (cached) {
        results[key] = cached;
      } else {
        missingKeys.push(key);
      }
    });

    return { results, missingKeys };
  }, [getCachedData]);

  /**
   * Batch set multiple items in cache
   */
  const setBatchCachedData = useCallback((cacheType, items) => {
    Object.entries(items).forEach(([key, data]) => {
      setCachedData(cacheType, key, data);
    });
  }, [setCachedData]);

  // Cache stats available but not logged to reduce console noise

  const value = {
    getCachedData,
    setCachedData,
    hasCachedData,
    clearCache,
    getCacheStats,
    getBatchCachedData,
    setBatchCachedData
  };

  return (
    <CacheContext.Provider value={value}>
      {children}
    </CacheContext.Provider>
  );
};

// Export cache types for consistency
export const CACHE_TYPES = {
  USER_PROFILES: 'userProfiles',
  CHAT_METADATA: 'chatMetadata',
  PRESENCE: 'presence',
  MESSAGES: 'messages',
  UNREAD_COUNTS: 'unreadCounts'
};
